<template>
  <div class="form-explorer">

    <!-- Message if no data loaded -->
    <p v-if="!groups.length" class="form-control-plaintext ml-2">
      {{ noFormsMessage }}
    </p>

    <!-- The form groups -->
    <form-group v-else v-for="group in groups"
                :key="getGroupKey(group)"
                :group="group"
                :tab="tab"
                :show-all-groups-mode="showAllGroupsMode"
                :manage-groups-allowed="manageGroupsAllowed"
                :no-forms-message="noFormsMessage"
                :nesting-level="0"
                :view-mode="viewMode"
                :expanded="expanded"
                @selectedFormsUpdated="updateSelectedForms(getGroupKey(group), $event)"
                @selectedGroupsUpdated="updateSelectedGroups(getGroupKey(group), $event)"
    >
    </form-group>

    <!-- Footer actions: -->

    <!-- List mode actions -->
    <template v-if="!isGroupPage">
      <div v-if="groups.length" class="row mt-2">
        <div class="col-md-12 bg-muted">
          <div class="text-muted ml-3">Forms selected: {{ selectedFormIds.length }}</div>
          <!-- Send email button -->
          <div class="form-group" v-if="sendEmailAllowed">
            <button class="btn btn-primary ml-2 ml-2" @click="$refs.emailComposer.showModal()" :disabled="!selectedFormIds.length">
              <i class="fa fa-fw fa-edit"></i> Send via Email
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Single group page mode actions -->
    <div v-else class="form-list-header">
      <div class="text-muted ml-3">Forms selected: {{ selectedFormIds.length }}</div>

      <div class="row">

        <!-- Send email button -->
        <div class="col-md-6">
          <div v-if="sendEmailAllowed" class="form-group">
            <button class="btn btn-primary ml-3" @click="$refs.emailComposer.showModal()" :disabled="!selectedFormIds.length">
              <i class="fa fa-fw fa-edit"></i> Send via Email
            </button>
          </div>
        </div>

        <!-- Edit group link -->
        <div class="col-md-6">
          <div v-if="manageGroupsAllowed" class="float-right">
            <a :href="groups[0] ? groups[0].edit_link : ''" class="btn btn-sm btn-primary mr-3">
              <i class="fa fa-fw fa-pencil"></i> Edit
            </a>
          </div>
        </div>

      </div>
    </div>

    <!-- Email composer -->
    <email-compose
        ref="emailComposer"
        :forms="selectedFormIds"
        :allow-manage-lookup-email-item="allowManageLookupEmailItem"
        :sender-options="senderOptions"
        :sender-signatures="senderSignatures"
        :attachments-max-size="attachmentsMaxSize"
    ></email-compose>

  </div>
</template>

<script>
import FormGroup from "./FormGroup";
import EmailCompose from "./../EmailCompose";

/**
 * This widget shows dynamic the Form group tree, including all the forms and subgroups.
 */
export default {

  /**
   * Input properties.
   *
   * Usage examples:
   *
   * <form-explorer
   *   :initial-groups="{{ json_encode($groups) }}"
   *   tab="{{ $selectedTab->key }}"
   *   no-forms-message="No forms"
   *   show-all-groups
   *   manage-groups-allowed
   * >
   * </form-explorer>
   */
  props: {

    // Initial top level group list.
    initialGroups: {
      type: Array,
      default: () => {return [];}
    },

    // Current tab key.
    tab: {
      type: String,
      default: 'active'
    },

    // The message to be shown if no data loaded.
    noFormsMessage: {
      type: String,
      default: 'No forms'
    },

    // Show all groups, including inactive.
    showAllGroupsMode: {
      type: Boolean,
      default: false
    },

    // Indicates if user can manage the groups.
    manageGroupsAllowed: {
      type: Boolean,
      default: false
    },

    // Indicates if user can send the forms by email.
    sendEmailAllowed: {
      type: Boolean,
      default: false
    },

    // Show widget in "view mode" (no "edit" buttons, etc).
    viewMode: {
      type: Boolean,
      default: false
    },

    // Show widget for one form group page.
    isGroupPage: {
      type: Boolean,
      default: false
    },

    // Show groups expanded by default.
    expanded: {
      type: Boolean,
      default: false
    },

    // Allow user to manage lookup email list items.
    allowManageLookupEmailItem: {
      type: Boolean,
      default: false
    },

    // Email senders.
    senderOptions: {
      type: Array,
      default: function () { return [] }
    },

    // Sender signatures.
    senderSignatures: {
      type: Object,
      default: function () { return [] }
    },

    // Modal dialog title.
    attachmentsMaxSize: {
      type: Number,
      default: 5242880
    },

  },

  /**
   * Variables (models).
   */
  data() {
    return {
      // The form groups with all related data.
      groups: [],

      // Selected form ids by groups.
      selectedFormIdsByGroups: [],

      // Selected subgroup keys by groups.
      selectedGroupKeysByGroups: [],
    }
  },

  /**
   * Functions (methods).
   */
  methods: {

    /**
     * Update selected form ids.
     */
    updateSelectedForms(groupKey, newFormIds) {
      let index = this.selectedFormIdsByGroups.findIndex(groupInfo => groupInfo.groupKey === groupKey);
      if (index !== -1) this.selectedFormIdsByGroups[index].selectedFormIds = newFormIds;
    },

    /**
     * Update selected groups.
     */
    updateSelectedGroups(groupKey, newGroupKeys) {
      let index = this.selectedGroupKeysByGroups.findIndex(groupInfo => groupInfo.groupKey === groupKey);
      if (index !== -1) this.selectedGroupKeysByGroups[index].selectedGroupKeys = newGroupKeys;
    },

    /**
     * Generate a group key.
     */
    getGroupKey(group) {
      return group.id + (group.zip_item ? '_' + group.zip_item.index : '');
    },

  },

  /**
   * Computed values
   */
  computed: {

    /**
     * Generates all selected form ids array.
     */
    selectedFormIds() {
      let selectedIds = [];
      // Merge selected form ids from all groups.
      this.selectedFormIdsByGroups.forEach(groupInfo => {selectedIds = [...selectedIds, ...groupInfo.selectedFormIds]});
      // Remove duplicates.
      [...new Set(selectedIds)];
      return selectedIds;
    },

    /**
     * Generates all selected group keys array.
     */
    selectedGroupKeys() {
      let selectedKeys = [];
      // Merge selected group keys from all groups.
      this.selectedGroupKeysByGroups.forEach(groupInfo => {selectedKeys = [...selectedKeys, ...groupInfo.selectedGroupKeys]});
      // Remove duplicates.
      [...new Set(selectedKeys)];
      return selectedKeys;
    },

  },

  /**
   * Component initialization.
   */
  mounted: function () {
    this.groups = this.initialGroups;
    this.selectedGroupKeysByGroups = [];
    this.selectedFormIdsByGroups = [];
    this.groups.forEach(group => {this.selectedGroupKeysByGroups.push({groupKey: this.getGroupKey(group), selectedGroupKeys: []});});
    this.groups.forEach(group => {this.selectedFormIdsByGroups.push({groupKey: this.getGroupKey(group), selectedFormIds: []});});
  },

  /**
   * Used components.
   */
  components: {
    FormGroup
  },
}
</script>
